import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  MessageSquare,
  Zap,
  Users,
  ShieldCheck,
  Phone,
  MessageCircle,
  Mail,
  ArrowRight,
  MapPin,
  Clock,
  Send,
  Heart,
  CarFront,
  CalendarDays,
  Tag,
  Lightbulb,
  FileText,
  ListChecks,
  Star,
  CheckCircle2,
  Copy,
  Check,
  X,
  Hash,
  Search,
  ChevronDown,
} from "lucide-react";
import { ContactHero } from "@/components/ContactHero";
import { ContactVisitSections } from "@/components/ContactVisitSections";
import { CtaBand } from "@/components/CtaBand";
import { Script } from "@/components/Script";
import { CONTACT, waLink, telLink } from "@/config";
import { useVehicles } from "@/hooks/useVehicles";
import { supabase } from "@/lib/supabase";
import { matchesVehicleSearch, publicVehicles } from "@/utils/vehicleSearch";
import "./Contact.css";
import "@/components/ContactRefinements.css";

const chatAbout = [
  { icon: CarFront, label: "Vehicle availability and pricing" },
  { icon: MessageSquare, label: "Model advice and recommendations" },
  { icon: CalendarDays, label: "Book a test drive or viewing" },
  { icon: FileText, label: "Finance and budgeting questions" },
  { icon: Tag, label: "Trade-in guidance" },
  { icon: Lightbulb, label: "Finding the right vehicle for your needs" },
  { icon: Star, label: "Any other questions you may have" },
];

const whyCards = [
  { icon: Zap, title: "Fast Response", desc: "I aim to reply within a few hours." },
  { icon: Users, title: "Deal Directly With Me", desc: "You'll always work with me, Simphiwe." },
  { icon: ShieldCheck, title: "Honest Advice", desc: "Straightforward, transparent guidance you can trust." },
  { icon: Heart, title: "No Pressure", desc: "Take your time — I'm here to support your journey." },
];

const initialContactForm = () => {
  const params = new URLSearchParams(window.location.search);
  const subject = params.get("subject")?.trim() || "";
  const suppliedMessage = params.get("message")?.trim() || "";
  return {
    name: "",
    phone: "",
    email: "",
    contactMethod: "",
    vehicle: params.get("vehicle") || "",
    enquiryType: params.get("enquiryType") || "",
    subject: subject || "Website enquiry",
    message: suppliedMessage || (subject ? `Subject: ${subject}\n\n` : ""),
  };
};

interface SubmissionSuccessData {
  referenceCode: string;
  name: string;
  email: string;
  vehicle?: string;
  enquiryType?: string;
  receiptSent?: boolean;
}

const Contact: React.FC = () => {
  const [form, setForm] = useState(initialContactForm);
  const [consent, setConsent] = useState(false);
  const [honeypot, setHoneypot] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submissionSuccess, setSubmissionSuccess] = useState<SubmissionSuccessData | null>(null);
  const [copied, setCopied] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  const adjustTextareaHeight = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    const minHeight = 140;
    el.style.height = `${Math.max(el.scrollHeight, minHeight)}px`;
  }, []);

  useEffect(() => {
    adjustTextareaHeight();
  }, [form.message, adjustTextareaHeight]);

  useEffect(() => {
    window.addEventListener("resize", adjustTextareaHeight);
    return () => window.removeEventListener("resize", adjustTextareaHeight);
  }, [adjustTextareaHeight]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (honeypot) return; // spam bot caught
    if (!form.name || !form.phone || !form.email || !form.message) {
      toast.error("Please fill in all required fields.");
      return;
    }
    if (!consent) {
      toast.error("Please agree to the processing of your personal information.");
      return;
    }
    const apiBase = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === "development" ? "http://localhost:8000" : "");
    const fallbackHex = Math.floor((1 + Math.random()) * 0x100000000).toString(16).substring(1).toUpperCase();
    let refCode = fallbackHex;
    let emailSent = false;
    let receiptSent = false;
    let stored = false;
    const storeEnquiry = async (deliveryStatus: "sent" | "failed") => {
      const { error } = await supabase.rpc("submit_contact_enquiry", {
        p_reference_code: refCode,
        p_name: form.name,
        p_phone: form.phone,
        p_email: form.email,
        p_preferred_contact_method: form.contactMethod,
        p_vehicle: form.vehicle,
        p_enquiry_type: form.enquiryType,
        p_subject: form.subject,
        p_message: form.message,
        p_delivery_status: deliveryStatus,
        p_receipt_sent: receiptSent,
      });
      if (error) throw error;
      stored = true;
    };
    setSubmitting(true);
    try {
      const response = await fetch(`${apiBase.replace(/\/$/, "")}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name,
          phone: form.phone,
          email: form.email,
          preferred_contact_method: form.contactMethod || null,
          vehicle: form.vehicle || null,
          enquiry_type: form.enquiryType || null,
          subject: form.subject,
          message: form.message,
        }),
      });
      const result: { email_sent?: boolean; receipt_sent?: boolean; reference_code?: string } | null = await response.json().catch(() => null);
      refCode = result?.reference_code || fallbackHex;
      emailSent = response.ok && Boolean(result?.email_sent);
      receiptSent = Boolean(result?.receipt_sent);
      await storeEnquiry(emailSent ? "sent" : "failed");
      if (!response.ok) throw new Error("Unable to send enquiry");
      if (!emailSent) throw new Error("The enquiry email was not accepted for delivery");

      setSubmissionSuccess({
        referenceCode: refCode,
        name: form.name,
        email: form.email,
        vehicle: form.vehicle,
        enquiryType: form.enquiryType,
        receiptSent,
      });

      toast.success(`Message sent! Reference: #${refCode}`);
      setForm({ name: "", phone: "", email: "", contactMethod: "", vehicle: "", enquiryType: "", subject: "Website enquiry", message: "" });
      setConsent(false);
    } catch {
      if (!stored) {
        try { await storeEnquiry(emailSent ? "sent" : "failed"); }
        catch { /* The email error remains the primary message shown to the customer. */ }
      }
      toast.error("Your message could not be sent. Please try again or contact Simphiwe directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="contact-page">
      <ContactHero />

      {/* Form + contact directly */}
      <section id="contact-enquiry" className="contact-form-screen">
        <div className="contact-form-shell">
        {/* Form */}
        <div className="contact-form-card" id="get-in-touch">
          {submissionSuccess && (
            <div className="contact-success-overlay" role="dialog" aria-modal="true" aria-labelledby="contact-success-title">
              <button
                type="button"
                className="contact-success-close-btn"
                onClick={() => setSubmissionSuccess(null)}
                aria-label="Close confirmation message"
                title="Close"
                data-testid="contact-success-close"
              >
                <X />
              </button>

              <div className="contact-success-body">
                <div className="contact-success-icon-badge">
                  <CheckCircle2 />
                </div>
                <p className="contact-section-kicker"><i /> Confirmation</p>
                <h3 id="contact-success-title">Your Form Was Submitted!</h3>
                <p className="contact-success-text">
                  Thank you, <strong>{submissionSuccess.name}</strong>. Your enquiry has been received and forwarded directly to <strong>Simphiwe Fudwana</strong>.
                </p>

                <div className="contact-reference-panel">
                  <div className="contact-reference-label">
                    <Hash />
                    <span>Your Reference Code</span>
                  </div>
                  <div className="contact-reference-code-box">
                    <span className="contact-reference-code-text">#{submissionSuccess.referenceCode}</span>
                    <button
                      type="button"
                      className="contact-copy-ref-btn"
                      onClick={() => {
                        navigator.clipboard.writeText(submissionSuccess.referenceCode);
                        setCopied(true);
                        toast.success("Reference code copied to clipboard!");
                        setTimeout(() => setCopied(false), 2000);
                      }}
                      title="Copy reference code"
                      data-testid="contact-copy-ref"
                    >
                      {copied ? <Check /> : <Copy />}
                      <span>{copied ? "Copied" : "Copy"}</span>
                    </button>
                  </div>
                  <p className="contact-reference-hint">
                    This reference code has been sent to your receipt email (<strong>{submissionSuccess.email}</strong>) and to Simphiwe.
                  </p>
                </div>

                <div className="contact-success-details">
                  {submissionSuccess.vehicle && (
                    <div className="contact-success-detail-row">
                      <CarFront />
                      <span>Vehicle: <strong>{submissionSuccess.vehicle}</strong></span>
                    </div>
                  )}
                  {submissionSuccess.enquiryType && (
                    <div className="contact-success-detail-row">
                      <ListChecks />
                      <span>Enquiry type: <strong>{submissionSuccess.enquiryType}</strong></span>
                    </div>
                  )}
                  <div className="contact-success-detail-row">
                    <Clock />
                    <span>Simphiwe usually responds within a few hours (or 1 business day).</span>
                  </div>
                </div>

                <div className="contact-success-actions">
                  <button
                    type="button"
                    className="contact-success-dismiss-btn"
                    onClick={() => setSubmissionSuccess(null)}
                    data-testid="contact-success-dismiss"
                  >
                    <span>Close & Return to Form</span>
                    <ArrowRight />
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className={`contact-form-content ${submissionSuccess ? "is-hidden" : ""}`}>
            <div className="contact-form-heading">
              <span className="contact-form-title-icon">
                <Mail />
              </span>
              <div>
                <p className="contact-section-kicker"><i /> Get in touch</p>
                <h2>Send Me a Message</h2>
                <p>Enquire about vehicle availability, pricing, trade-ins, bookings or general advice.<br />Fill in the form below and I’ll get back to you as soon as possible.</p>
              </div>
            </div>

            <form onSubmit={submit} className="contact-design-form">
              <input
                type="text"
                value={honeypot}
                onChange={(e) => setHoneypot(e.target.value)}
                className="hidden"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
              />
              <div className="contact-form-fields">
                <FormField label="Full Name" required icon={Users}><input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your full name" data-testid="contact-name" /></FormField>
                <FormField label="Phone Number" required icon={Phone}><input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="e.g. 068 103 1545" data-testid="contact-phone" /></FormField>
                <FormField label="Email Address" required icon={Mail}><input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@example.com" data-testid="contact-email" /></FormField>
                <FormField label="Preferred Contact Method" icon={MessageSquare}><select value={form.contactMethod} onChange={(e) => setForm({ ...form, contactMethod: e.target.value })}><option value="">Select a method</option><option>Phone call</option><option>WhatsApp</option><option>Email</option></select></FormField>
                <VehicleSearchField value={form.vehicle} onChange={(vehicle) => setForm({ ...form, vehicle })} />
                <FormField label="Enquiry Type" icon={ListChecks}><select value={form.enquiryType} onChange={(e) => setForm({ ...form, enquiryType: e.target.value })}><option value="">Select an enquiry type</option><option>Vehicle availability</option><option>Book a viewing or test drive</option><option>Finance and budgeting</option><option>Trade-in</option><option>Vehicle recommendation</option><option>General enquiry</option></select></FormField>
              </div>
              <div className="contact-form-message">
                <label>What can I help you with? <b>*</b></label>
                <span>
                  <FileText />
                  <textarea
                    ref={textareaRef}
                    value={form.message}
                    onChange={(e) => {
                      setForm({ ...form, message: e.target.value });
                      e.target.style.height = "auto";
                      e.target.style.height = `${Math.max(e.target.scrollHeight, 140)}px`;
                    }}
                    rows={5}
                    maxLength={1000}
                    placeholder="Tell me how I can help you... (e.g. vehicle details, availability, pricing, trade-in, booking a viewing or test drive, or any other questions.)"
                    data-testid="contact-message"
                  />
                </span>
                <small>{form.message.length}/1000</small>
              </div>
              <label className="contact-form-consent">
                <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} data-testid="contact-consent" />
                <span>
                  I agree to the processing of my personal information in accordance with the{" "}
                  <Link to="/privacy/">Privacy Policy</Link>.
                </span>
              </label>
              <button type="submit" className="contact-form-submit" data-testid="contact-submit" disabled={submitting}>
                <Send /> {submitting ? "Sending…" : "Send Message"} <ArrowRight />
              </button>
            </form>
          </div>
          <div className={`contact-form-trust ${submissionSuccess ? "is-hidden" : ""}`}><div><ShieldCheck /><span><strong>Your information is secure</strong><small>We respect your privacy.</small></span></div><div><Clock /><span><strong>Quick response</strong><small>Usually within 1 business day.</small></span></div><div><Users /><span><strong>No obligation</strong><small>Just helpful, honest advice.</small></span></div></div>
        </div>

        {/* Contact directly */}
        <div className="contact-direct-column">
          <div className="contact-direct-heading">
            <p className="contact-section-kicker"><i /> Let’s connect</p>
            <h2>Contact Me Directly</h2>
            <p>Prefer to talk now? Here are the easiest ways to reach me.</p>
          </div>

          <div className="contact-direct-links">
            <DirectLink href={telLink} icon={Phone} title="Call Me" detail={CONTACT.phone} description="Speak directly for the quickest assistance." testId="contact-call" />
            <DirectLink href={waLink()} icon={MessageCircle} title="WhatsApp Me" detail="Chat with me directly" description="Quick questions, photos or more info." testId="contact-whatsapp" />
            <DirectLink href={`mailto:${CONTACT.email}`} icon={Mail} title="Email Me" detail={CONTACT.email} description="Send through your enquiry anytime." testId="contact-email-direct" />
          </div>

          <div className="contact-help-card">
            <div className="contact-help-heading"><h3>What I can help with</h3><span>Driven by people <i /></span></div>
            <ul>
              {chatAbout.map(({ icon: Icon, label }) => (
                <li key={label}><Icon /> {label}</li>
              ))}
            </ul>
          </div>
          <div className="contact-support-panel"><div><p className="contact-section-kicker"><i /> Here for you</p><h3>Straightforward advice.<br />A smoother journey.</h3></div><ul><li><Zap />Quick response</li><li><Users />Flexible ways to connect</li><li><ShieldCheck />Honest, expert advice</li></ul><img src="/images/buying-journey-rav4.png" alt="Toyota vehicle" /></div>
          <div className="contact-direct-script"><Script size="text-2xl">Good People. Bigger Journeys.</Script></div>
        </div>
        </div>
      </section>

      <ContactVisitSections />

      <CtaBand
        eyebrow="Prefer a quick chat?"
        title="Message Me on WhatsApp"
        desc="It's the fastest and easiest way to get in touch. Ask questions, check vehicle availability, or just have a chat — I'm here for you."
        variant="whatsapp"
      />
    </main>
  );
};

const FormField: React.FC<{ label: string; required?: boolean; icon: any; children: React.ReactElement }> = ({ label, required, icon: Icon, children }) => (
  <label className="contact-form-field">
    <span>{label} {required && <b>*</b>}</span>
    <i><Icon />{children}</i>
  </label>
);

const VehicleSearchField: React.FC<{ value: string; onChange: (value: string) => void }> = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const { vehicles } = useVehicles();
  const inventory = publicVehicles(vehicles);
  const query = value.trim().toLowerCase();
  const matches = useMemo(() => {
    const terms = query.split(/\s+/).filter(Boolean);
    return inventory.filter((vehicle) => {
      if (!terms.length) return true;
      return matchesVehicleSearch(vehicle, terms.join(" "));
    });
  }, [inventory, query]);

  return (
    <div className="contact-form-field contact-vehicle-field">
      <span id="contact-vehicle-label">Vehicle of Interest</span>
      <div
        className="contact-vehicle-combobox"
        onBlur={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setOpen(false);
        }}
      >
        <CarFront aria-hidden="true" />
        <input
          type="text"
          value={value}
          onChange={(event) => { onChange(event.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={(event) => {
            if (event.key === "Escape") setOpen(false);
            if (event.key === "ArrowDown" && !open) setOpen(true);
            if (event.key === "Enter" && open && matches.length) {
              event.preventDefault();
              onChange(matches[0].fullTitle);
              setOpen(false);
            }
          }}
          role="combobox"
          aria-labelledby="contact-vehicle-label"
          aria-controls="contact-vehicle-options"
          aria-expanded={open}
          aria-autocomplete="list"
          autoComplete="off"
          placeholder="Search vehicle or model (optional)"
          data-testid="contact-vehicle"
        />
        {value ? (
          <button type="button" className="contact-vehicle-clear" onClick={() => { onChange(""); setOpen(true); }} aria-label="Clear selected vehicle"><X /></button>
        ) : (
          <button type="button" className="contact-vehicle-toggle" onClick={() => setOpen((shown) => !shown)} aria-label="Show vehicle options"><ChevronDown /></button>
        )}
        {open && <div id="contact-vehicle-options" className="contact-vehicle-options" role="listbox">
          {matches.map((vehicle) => <button
            key={vehicle.id}
            type="button"
            role="option"
            aria-selected={vehicle.fullTitle === value}
            onClick={() => { onChange(vehicle.fullTitle); setOpen(false); }}
          >
            <span>{vehicle.fullTitle}</span>
            <small>R {vehicle.price.toLocaleString("en-ZA")} · {vehicle.mileage.toLocaleString("en-ZA")} km · Stock {vehicle.id}</small>
          </button>)}
          {!matches.length && <p>No matching vehicles found. Try another name, model or stock number.</p>}
        </div>}
      </div>
    </div>
  );
};

const DirectLink: React.FC<{ href: string; icon: any; title: string; detail: string; description: string; testId: string }> = ({ href, icon: Icon, title, detail, description, testId }) => (
  <a href={href} className="contact-direct-link" data-testid={testId}>
    <span className="contact-direct-icon"><Icon /></span>
    <span><strong>{title}</strong><b>{detail}</b><small>{description}</small></span>
    <ArrowRight />
  </a>
);

const InfoBlock: React.FC<{ icon: any; title: string; lines: string[] }> = ({ icon: Icon, title, lines }) => (
  <div className="flex gap-3">
    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
      <Icon className="h-5 w-5" />
    </span>
    <div>
      <h4 className="text-sm font-bold text-slate-900">{title}</h4>
      {lines.map((l) => (
        <p key={l} className="text-sm text-slate-500">{l}</p>
      ))}
    </div>
  </div>
);

export default Contact;
